import { Routes, Route, Navigate } from 'react-router-dom';
import { AppShell } from '@/components/layout/AppShell';
import { ProtectedRoute } from '@/router/ProtectedRoute';
import { RequireModule } from '@/router/RequireModule';
import { AuditorProtectedRoute } from '@/router/AuditorProtectedRoute';
import { AuditorShell } from '@/pages/auditor/AuditorShell';
import { AuditorActivatePage } from '@/pages/auditor/AuditorActivatePage';
import { AuditorDashboardPage } from '@/pages/auditor/AuditorDashboardPage';
import { AuditorFrameworkListPage } from '@/pages/auditor/AuditorFrameworkListPage';
import { AuditorFrameworkDetailPage } from '@/pages/auditor/AuditorFrameworkDetailPage';
import { AuditorControlDetailPage } from '@/pages/auditor/AuditorControlDetailPage';
import { AuditorForbiddenPage } from '@/pages/auditor/AuditorForbiddenPage';
import { AuditorExpiredPage } from '@/pages/auditor/AuditorExpiredPage';
import { AuditorRevokedPage } from '@/pages/auditor/AuditorRevokedPage';
import { AuditorAccessPage } from '@/pages/app/settings/AuditorAccessPage';
import { LoginPage } from '@/pages/LoginPage';
import { AcceptInvitationPage } from '@/pages/AcceptInvitationPage';
import { ReverifyPage } from '@/pages/ReverifyPage';
import { DashboardPage } from '@/pages/app/DashboardPage';
import { VendorsPage } from '@/pages/app/VendorsPage';
import { VendorDetailPage } from '@/pages/app/VendorDetailPage';
import { AssessmentsPage } from '@/pages/app/AssessmentsPage';
import { AssessmentDetailPage } from '@/pages/app/AssessmentDetailPage';
import { NotificationCenterPage } from '@/pages/app/NotificationCenterPage';
import { ControlTrackerPage } from '@/pages/app/ControlTrackerPage';
import { ControlsPage } from '@/pages/app/ControlsPage';
import { ControlsWorkspacePage } from '@/pages/app/ControlsWorkspacePage';
import ControlLibraryPage from '@/pages/app/ControlLibraryPage';
import LibraryControlSheet from '@/pages/app/LibraryControlSheet';
import FrameworkHierarchyPage from '@/pages/app/FrameworkHierarchyPage';
import { FrameworksWorkspacePage } from '@/pages/app/FrameworksWorkspacePage';
import { TasksWorkspacePage } from '@/pages/app/TasksWorkspacePage';
import { ControlHubPage } from '@/pages/app/ControlHubPage';
import { AuditCenterPage } from '@/pages/app/AuditCenterPage';
import { EngagementDetailPage } from '@/pages/app/EngagementDetailPage';
import { ControlFullDetailPage } from '@/pages/app/ControlFullDetailPage';
import { SettingsHubPage } from '@/pages/app/settings/SettingsHubPage';
import { SupportContactsPage } from '@/pages/app/settings/SupportContactsPage';
import { ProfilePage } from '@/pages/app/settings/ProfilePage';
import { TeamPage } from '@/pages/app/settings/TeamPage';
import { NotificationsPage } from '@/pages/app/settings/NotificationsPage';
import { AssessmentReviewPage } from '@/pages/app/settings/AssessmentReviewPage';
import { AccountHygienePage } from '@/pages/app/settings/AccountHygienePage';
import { EvidenceRepositoriesPage } from '@/pages/app/settings/EvidenceRepositoriesPage';
import { EscalationContactsPage } from '@/pages/app/settings/EscalationContactsPage';
import { SupportVerificationPage } from '@/pages/app/settings/SupportVerificationPage';
import { IntegrationsPage } from '@/pages/app/settings/IntegrationsPage';
import { WebhooksPage } from '@/pages/app/settings/WebhooksPage';
import { AuditLogPage } from '@/pages/app/settings/AuditLogPage';
import { BillingPage } from '@/pages/app/settings/BillingPage';
import { BrandingPage } from '@/pages/app/settings/BrandingPage';
import { PlaceholderPage } from '@/pages/app/PlaceholderPage';
import { SsoConfigPage } from '@/pages/app/settings/SsoConfigPage';
import { SsoStartPage } from '@/pages/auth/sso/SsoStartPage';
import { SsoErrorPage } from '@/pages/auth/sso/SsoErrorPage';
import { SsoNotProvisionedPage } from '@/pages/auth/sso/SsoNotProvisionedPage';
import { SsoCompletePage } from '@/pages/auth/sso/SsoCompletePage';
import { TemplatesPage } from '@/pages/app/TemplatesPage';
import { HubTemplatesPage } from '@/pages/app/HubTemplatesPage';
import { TemplateDetailPage } from '@/pages/app/TemplateDetailPage';
import { TemplateAuthoringPage } from '@/pages/app/TemplateAuthoringPage';
import { CustomAuthoringOnboardingPage } from '@/pages/app/CustomAuthoringOnboardingPage';
import { ControlDetailPage } from '@/pages/app/ControlDetailPage';
import { ProvisioningRunPage } from '@/pages/app/ProvisioningRunPage';
import { EvidenceLibraryPage } from '@/pages/app/EvidenceLibraryPage';
import { RiskAcceptancesIndexPage } from '@/pages/risk-acceptances/RiskAcceptancesIndexPage';
import { RiskAcceptanceCreationPage } from '@/pages/risk-acceptances/RiskAcceptanceCreationPage';
import { RiskAcceptanceDetailPage } from '@/pages/risk-acceptances/RiskAcceptanceDetailPage';
import { OrgRiskAcceptancesIndexPage } from '@/pages/org-risk-acceptances/OrgRiskAcceptancesIndexPage';
import { OrgRiskAcceptanceCreationPage } from '@/pages/org-risk-acceptances/OrgRiskAcceptanceCreationPage';
import { OrgRiskAcceptanceDetailPage } from '@/pages/org-risk-acceptances/OrgRiskAcceptanceDetailPage';
import { ComplianceDashboardPage } from '@/pages/dashboards/ComplianceDashboardPage';
import { MfaEnrollmentPage } from '@/pages/MfaEnrollmentPage';
import { ForgotPasswordPage } from '@/pages/ForgotPasswordPage';
import { ResetPasswordPage } from '@/pages/ResetPasswordPage';
import { ForgotMfaPage } from '@/pages/ForgotMfaPage';
import { ResetMfaPage } from '@/pages/ResetMfaPage';
import { NotFound } from '@/pages/NotFound';

export function AppRouter() {
  return (
    <Routes>
      {/* Public */}
      <Route path="/login" element={<LoginPage />} />
      <Route path="/forgot-password" element={<ForgotPasswordPage />} />
      <Route path="/reset-password" element={<ResetPasswordPage />} />
      <Route path="/forgot-mfa" element={<ForgotMfaPage />} />
      <Route path="/reset-mfa" element={<ResetMfaPage />} />
      <Route path="/accept-invitation" element={<AcceptInvitationPage />} />
      <Route path="/auth/reverify" element={<ReverifyPage />} />
      <Route path="/auth/mfa-enrollment" element={<MfaEnrollmentPage />} />

      {/* SSO public routes — must be outside ProtectedRoute */}
      <Route path="/auth/sso/start" element={<SsoStartPage />} />
      <Route path="/auth/sso/error" element={<SsoErrorPage />} />
      <Route path="/auth/sso/not-provisioned" element={<SsoNotProvisionedPage />} />
      <Route path="/auth/sso/complete" element={<SsoCompletePage />} />

      {/* Auditor portal — outside ProtectedRoute (own session) */}
      <Route path="/auditor/activate/:activationToken?" element={<AuditorActivatePage />} />
      <Route path="/auditor/forbidden" element={<AuditorForbiddenPage />} />
      <Route path="/auditor/expired" element={<AuditorExpiredPage />} />
      <Route path="/auditor/revoked" element={<AuditorRevokedPage />} />
      <Route element={<AuditorProtectedRoute />}>
        <Route element={<AuditorShell />}>
          <Route path="/auditor" element={<Navigate to="/auditor/dashboard" replace />} />
          <Route path="/auditor/dashboard" element={<AuditorDashboardPage />} />
          <Route path="/auditor/frameworks" element={<AuditorFrameworkListPage />} />
          <Route path="/auditor/frameworks/:frameworkId" element={<AuditorFrameworkDetailPage />} />
          <Route path="/auditor/frameworks/:frameworkId/controls/:controlId" element={<AuditorControlDetailPage />} />
        </Route>
      </Route>

      {/* Protected — wrapped in AppShell */}
      <Route element={<ProtectedRoute />}>
        <Route element={<AppShell />}>
          <Route path="/" element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />

          {/* Notifications center (NOTIF.4) */}
          <Route path="/notifications" element={<NotificationCenterPage />} />

          {/* Dashboards */}
          {/* ⚠ RET-10: the Enterprise Risk Dashboard is RETIRED — a bookmark lands on Home (its api is a 404 tombstone). */}
          <Route path="/dashboards/enterprise-risk" element={<Navigate to="/dashboard" replace />} />
          {/* ⚠ RET-10 (D-RR-SURFACES-MODULE-GATED): Compliance-gated here, in the menu and at the api. */}
          <Route element={<RequireModule module="compliance" />}>
            <Route path="/dashboards/compliance" element={<ComplianceDashboardPage />} />
          </Route>

          {/* TPRM */}
          <Route path="/vendors" element={<VendorsPage />} />
          <Route path="/vendors/new" element={<VendorsPage />} />
          <Route path="/vendors/:id" element={<VendorDetailPage />} />
          <Route path="/vendors/:id/assessments" element={<VendorDetailPage />} />
          <Route path="/assessments" element={<AssessmentsPage />} />
          <Route path="/assessments/:id" element={<AssessmentDetailPage />} />
          {/* ⚠ RET-10 (D-RR-SURFACES-MODULE-GATED): the Vendor Risk Register is part of VRM, the Risk Register (org risk
              responses) part of Compliance — gated here, in the menu and at the api mount. */}
          <Route element={<RequireModule module="vrm" />}>
            <Route path="/risk-acceptances" element={<RiskAcceptancesIndexPage />} />
            <Route path="/risk-acceptances/new" element={<RiskAcceptanceCreationPage />} />
            <Route path="/risk-acceptances/:id" element={<RiskAcceptanceDetailPage />} />
          </Route>
          <Route element={<RequireModule module="compliance" />}>
            <Route path="/org-risk-acceptances" element={<OrgRiskAcceptancesIndexPage />} />
            <Route path="/org-risk-acceptances/new" element={<OrgRiskAcceptanceCreationPage />} />
            <Route path="/org-risk-acceptances/:id" element={<OrgRiskAcceptanceDetailPage />} />
          </Route>
          <Route path="/templates" element={<TemplatesPage />} />
          <Route path="/templates/hub" element={<HubTemplatesPage />} />
          <Route path="/templates/new" element={<CustomAuthoringOnboardingPage />} />
          <Route path="/templates/:id" element={<TemplateDetailPage />} />
          <Route path="/templates/:id/edit" element={<TemplateAuthoringPage />} />

          {/* Compliance */}
          {/*
            ⚠ TC6a — THE LEGACY COMPLIANCE ENTRY POINT IS REMOVED.
            `/compliance` redirects to the rebuilt Frameworks workspace on cts_ + ccl_. A REDIRECT
            rather than a deletion, for two reasons: a bookmarked URL should land somewhere useful
            rather than 404, and the legacy pages behind this path (evidence, exceptions,
            my-assignments, workload, controls) are still routed until the drop — deleting the
            parent path would not remove them, it would only make this one URL dead.
            ⓘ ComplianceOverviewPage is now unreferenced; it comes down with the retirement slice,
            not here, so the parallel-run fallback survives until its replacement is proven.
          */}
          <Route path="/compliance" element={<Navigate to="/compliance/workspace/frameworks" replace />} />
          <Route path="/compliance/controls/:frameworkId/:controlId" element={<ControlDetailPage />} />
          <Route path="/compliance/controls/:frameworkId" element={<ControlTrackerPage />} />
          <Route path="/compliance/controls/detail/:implementationId" element={<ControlFullDetailPage />} />
          <Route path="/compliance/controls" element={<ControlsPage />} />
          {/*
            TC2 — the rebuilt Controls workspace and the Control Detail hub (cts_ + ccl_).
            ⚠ THE /workspace/ SEGMENT IS LOAD-BEARING (Trap #11 — route shadowing). The three
            legacy routes above own /compliance/controls/:frameworkId and run in PARALLEL until
            TC6; a bare /compliance/controls/:reference would be swallowed by the first of them.
            A fixed extra segment cannot be matched by any of those patterns.
          */}
          {/* TC3 — the Frameworks workspace, beside Controls under the same shadow-proof segment. */}
          <Route path="/compliance/workspace/frameworks" element={<FrameworksWorkspacePage />} />
          {/* TC4 — the Tasks workspace and the program surfaces, under the same shadow-proof segment. */}
          <Route path="/compliance/workspace/tasks" element={<TasksWorkspacePage />} />
          <Route path="/compliance/workspace/controls" element={<ControlsWorkspacePage />} />
          <Route path="/compliance/workspace/controls/:reference" element={<ControlHubPage />} />
          {/* EVIDENCE P1.2 — the provisioning run record (the report for a queued run). */}
          <Route path="/compliance/workspace/evidence/runs/:runId" element={<ProvisioningRunPage />} />
          {/* EVIDENCE P1.2 — connect the Evidence library (onboarding). */}
          <Route path="/compliance/workspace/evidence/library" element={<EvidenceLibraryPage />} />
          {/* ⚠ A SIBLING SEGMENT, not /workspace/controls/library — that would sit under the
              `:reference` route above and be read as a control reference (Trap #11, route
              shadowing). The breadcrumb Design specified says `compliance / control-library`,
              which is the same shape. */}
          {/* ⚠ The DETAIL WINDOW is a CHILD of the library, rendered through its <Outlet/>: the list stays
              mounted beneath it (search and filters survive), a link to one control works, Back closes it. */}
          <Route path="/compliance/control-library" element={<ControlLibraryPage />}>
            <Route path=":reference" element={<LibraryControlSheet />} />
          </Route>
          {/* ⚠ A DETAIL under the frameworks list, which is exact — no shadowing. Read-only:
              the framework's own structure, drilled into from its card. */}
          <Route path="/compliance/workspace/frameworks/:reference" element={<FrameworkHierarchyPage />} />
          {/* TC5 — the tenant-facing Audit Center, under the same shadow-proof segment.
              ⚠ NOT /auditor/* — that is the AUDITOR's own surface, with its own session and scope
              gate, and it is untouched by TC5. */}
          <Route path="/compliance/workspace/audit" element={<AuditCenterPage />} />
          <Route path="/compliance/workspace/audit/:tokenId" element={<EngagementDetailPage />} />
          {/*
            ⚠ TC6a — THE FOUR REMAINING LEGACY ENTRY POINTS, REDIRECTED.
            Evidence and Exceptions land on the TAB that replaced them, which is why the tab is
            addressable at all: sending them to the workspace's default tab would be a destination
            that exists but is wrong — worse than a dead link, because nothing looks broken.
            My Tasks and Team Queue both become the two-section Tasks workspace: it replaced both,
            and the sections are on one page by design.
            ⓘ Redirects, not deletions — the legacy pages stay routed until the drop, so the
            parallel-run fallback survives until its replacement is proven.
          */}
          <Route path="/compliance/evidence" element={<Navigate to="/compliance/workspace/tasks?tab=evidence" replace />} />
          <Route path="/compliance/exceptions" element={<Navigate to="/compliance/workspace/tasks?tab=exceptions" replace />} />
          <Route path="/compliance/my-assignments" element={<Navigate to="/compliance/workspace/tasks" replace />} />
          <Route path="/compliance/workload" element={<Navigate to="/compliance/workspace/tasks" replace />} />

          {/* Settings */}
          <Route path="/settings" element={<SettingsHubPage />} />
          <Route path="/settings/profile" element={<ProfilePage />} />
          <Route path="/settings/team" element={<TeamPage />} />
          <Route path="/settings/notifications" element={<NotificationsPage />} />
          <Route path="/settings/assessment-review" element={<AssessmentReviewPage />} />
          <Route path="/settings/account-hygiene" element={<AccountHygienePage />} />
          <Route path="/settings/escalation-contacts" element={<EscalationContactsPage />} />
          <Route path="/settings/support-verification" element={<SupportVerificationPage />} />
          <Route path="/settings/support-contacts" element={<SupportContactsPage />} />
          <Route path="/settings/integrations" element={<IntegrationsPage />} />
          {/* ⚠ super_admin-gated at the API AND on the page itself (RequireRole) — a direct-URL
              visit by a lesser role gets an on-route forbidden state, not a blank page. */}
          <Route path="/settings/evidence-repositories" element={<EvidenceRepositoriesPage />} />
          <Route path="/settings/webhooks" element={<WebhooksPage />} />
          <Route path="/settings/audit-log" element={<AuditLogPage />} />
          <Route path="/settings/billing" element={<BillingPage />} />
          <Route path="/settings/branding" element={<BrandingPage />} />
          <Route path="/settings/smtp" element={<PlaceholderPage title="SMTP" phase={5} />} />
          <Route path="/settings/auditor-access" element={<AuditorAccessPage />} />
          <Route path="/settings/sso" element={<SsoConfigPage />} />
        </Route>
      </Route>

      {/* Top-level 404 — any unmatched URL, authed or not (no auth logic; HARDEN-CATCHALL-REDIRECT-1). */}
      {/* Real routes still match their Route first; protected ones still send unauthed users to /login. */}
      <Route path="*" element={<NotFound />} />
    </Routes>
  );
}
